import { Component, input, signal, output, OnDestroy, inject, computed } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageModule } from 'primeng/message';
import { Subscription } from 'rxjs';
import { UpdateErrorType } from '../../core/models/update-error.model';
import { ApkDownloadService, DownloadProgress } from '@core/services/mobile/apk-download.service';
import { CommonModule } from '@angular/common';

/**
 * Configuration for the update dialog
 */
export interface UpdateDialogConfig {
  versionInfo: {
    version: string;
    isMandatory: boolean;
    apkUrl: string;
    filename: string;
    container: string;
    releaseDate: string;
  };
  visible: boolean;
}

/**
 * Update Dialog Component
 * Displays update notifications with real-time download progress
 */
@Component({
  selector: 'app-update-dialog',
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    ProgressBarModule,
    MessageModule,
    CommonModule
  ],
  templateUrl: './update-dialog.component.html',
  styleUrls: ['./update-dialog.component.scss']
})
export class UpdateDialogComponent implements OnDestroy {
  private downloadSubscription?: Subscription;
  private readonly apkDownloadService = inject(ApkDownloadService);

  /**
   * Error message mapping in Italian for each UpdateErrorType
   */
  private readonly ERROR_MESSAGES: Record<UpdateErrorType, string> = {
    [UpdateErrorType.NETWORK_ERROR]: 'Errore di connessione. Verifica la tua connessione internet e riprova.',
    [UpdateErrorType.VERSION_CHECK_FAILED]: 'Errore durante il controllo della versione. Riprova più tardi.',
    [UpdateErrorType.DOWNLOAD_FAILED]: 'Download fallito. Verifica la connessione e riprova.',
    [UpdateErrorType.INSUFFICIENT_STORAGE]: 'Spazio insufficiente. Libera spazio e riprova.',
    [UpdateErrorType.INSTALLATION_CANCELLED]: 'Installazione annullata.',
    [UpdateErrorType.FILE_OPERATION_FAILED]: 'Errore durante l\'operazione sul file. Riprova.',
    [UpdateErrorType.PERMISSION_DENIED]: 'Permessi necessari non concessi. Concedi i permessi nelle impostazioni.'
  };

  /**
   * Dialog configuration input
   */
  readonly config = input.required<UpdateDialogConfig>();

  /**
   * Current download progress state
   */
  readonly downloadState = signal<DownloadProgress | null>(null);

  /**
   * Error message to display to user
   */
  readonly errorMessage = signal<string>('');

  /**
   * Error type for determining error handling
   */
  readonly errorType = signal<UpdateErrorType | null>(null);

  /**
   * Flag to show retry button
   */
  readonly showRetryButton = signal<boolean>(false);

  /**
   * Computed: Is download in progress
   */
  readonly isDownloading = computed(() => {
    const state = this.downloadState();
    return state !== null && state.phase !== 'complete' && state.phase !== 'error';
  });

  /**
   * Computed: Download percentage for progress bar
   */
  readonly downloadProgress = computed(() => {
    return this.downloadState()?.percentage ?? 0;
  });

  /**
   * Computed: Status message for user feedback
   */
  readonly statusMessage = computed(() => {
    return this.downloadState()?.message ?? '';
  });

  /**
   * Computed: Show indeterminate progress bar
   */
  readonly showIndeterminate = computed(() => {
    const state = this.downloadState();
    if (!state) return false;
    return state.phase === 'preparing' || state.phase === 'saving' ||
      (state.phase === 'downloading' && state.total === 0);
  });

  /**
   * Computed: Formatted download speed
   */
  readonly downloadSpeed = computed(() => {
    const state = this.downloadState();
    if (!state || state.speed === 0) return '';
    return this.formatSpeed(state.speed);
  });

  /**
   * Computed: Formatted time remaining
   */
  readonly timeRemaining = computed(() => {
    const state = this.downloadState();
    if (!state || state.estimatedTimeRemaining <= 0 || !Number.isFinite(state.estimatedTimeRemaining)) return '';
    return this.formatTime(state.estimatedTimeRemaining);
  });

  /**
   * Event emitted when dialog is dismissed (optional updates only)
   */
  readonly dismissed = output<void>();

  /**
   * Event emitted when download completes successfully
   */
  readonly downloadComplete = output<void>();

  /**
   * Event emitted when download fails
   */
  readonly downloadError = output<string>();

  /**
   * Handle "Aggiorna Ora" button click
   */
  onUpdateNow(): void {
    try {
      // Reset state
      this.downloadState.set({
        loaded: 0,
        total: 0,
        percentage: 0,
        speed: 0,
        estimatedTimeRemaining: 0,
        phase: 'preparing',
        message: 'Preparazione download...'
      });
      this.errorMessage.set('');
      this.errorType.set(null);
      this.showRetryButton.set(false);

      // Start download
      this.downloadSubscription = this.apkDownloadService
        .downloadAndInstall(this.config().versionInfo.apkUrl, this.config().versionInfo.filename)
        .subscribe({
          next: (progress) => {
            this.downloadState.set(progress);
          },
          complete: () => {
            // Wait briefly to show completion message, then emit
            setTimeout(() => {
              this.downloadComplete.emit();
            }, 1000);
          },
          error: (error) => {
            console.error('❌ [UpdateDialog] Download failed:', error);
            this.handleDownloadError(error);
            this.downloadError.emit(error.message || 'Download failed');
          }
        });

    } catch (error) {
      console.error('❌ [UpdateDialog] Error starting download:', error);
      this.handleDownloadError(error);
      this.downloadError.emit(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Handle download errors
   */
  private handleDownloadError(error: any): void {
    let errorType: UpdateErrorType = UpdateErrorType.DOWNLOAD_FAILED;

    if (error?.type) {
      // Map DownloadErrorType to UpdateErrorType
      const typeMap: Record<string, UpdateErrorType> = {
        'NETWORK_ERROR': UpdateErrorType.NETWORK_ERROR,
        'INSUFFICIENT_STORAGE': UpdateErrorType.INSUFFICIENT_STORAGE,
        'PERMISSION_DENIED': UpdateErrorType.PERMISSION_DENIED,
        'FILE_OPERATION_FAILED': UpdateErrorType.FILE_OPERATION_FAILED,
        'TIMEOUT_ERROR': UpdateErrorType.NETWORK_ERROR
      };
      errorType = typeMap[error.type] || UpdateErrorType.DOWNLOAD_FAILED;
    } else if (error?.message) {
      const message = error.message.toLowerCase();
      if (message.includes('network') || message.includes('connection') || message.includes('timeout')) {
        errorType = UpdateErrorType.NETWORK_ERROR;
      } else if (message.includes('storage') || message.includes('space')) {
        errorType = UpdateErrorType.INSUFFICIENT_STORAGE;
      } else if (message.includes('permission')) {
        errorType = UpdateErrorType.PERMISSION_DENIED;
      } else if (message.includes('cancel')) {
        errorType = UpdateErrorType.INSTALLATION_CANCELLED;
      }
    }

    // Update error state
    this.errorType.set(errorType);
    this.errorMessage.set(this.ERROR_MESSAGES[errorType]);
    this.downloadState.set({
      loaded: 0,
      total: 0,
      percentage: 0,
      speed: 0,
      estimatedTimeRemaining: 0,
      phase: 'error',
      message: ''
    });

    // Show retry for recoverable errors
    const recoverableErrors = [
      UpdateErrorType.NETWORK_ERROR,
      UpdateErrorType.DOWNLOAD_FAILED,
      UpdateErrorType.FILE_OPERATION_FAILED
    ];
    this.showRetryButton.set(recoverableErrors.includes(errorType));

    console.error('❌ [UpdateDialog] Error type:', errorType);
  }

  /**
   * Handle retry button click
   */
  onRetry(): void {
    this.errorMessage.set('');
    this.errorType.set(null);
    this.showRetryButton.set(false);
    this.onUpdateNow();
  }

  /**
   * Handle cancel download
   */
  onCancelDownload(): void {
    this.apkDownloadService.cancelDownload();
    this.downloadState.set(null);
  }

  /**
   * Handle "Chiudi App" button click
   */
  async onExitApp(): Promise<void> {
    try {
      const { App } = await import('@capacitor/app');
      await App.exitApp();
    } catch (error) {
      console.error('❌ [UpdateDialog] Error exiting app:', error);
      window.close();
    }
  }

  /**
   * Handle "Più Tardi" button click
   */
  onDismiss(): void {
    // Cancel any ongoing download
    this.apkDownloadService.cancelDownload();

    if (this.downloadSubscription) {
      this.downloadSubscription.unsubscribe();
      this.downloadSubscription = undefined;
    }

    // Reset state
    this.downloadState.set(null);
    this.errorMessage.set('');
    this.errorType.set(null);
    this.showRetryButton.set(false);

    this.dismissed.emit();
  }

  /**
   * Handle dialog hide event
   */
  onDialogHide(): void {
    if (!this.config().versionInfo.isMandatory) {
      this.onDismiss();
    }
  }

  /**
   * Format bytes to human-readable string
   */
  private formatSpeed(bytesPerSecond: number): string {
    if (bytesPerSecond === 0) return '';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return Number.parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[Math.min(i, sizes.length - 1)];
  }

  /**
   * Format seconds to human-readable time
   */
  private formatTime(seconds: number): string {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  }

  ngOnDestroy(): void {
    this.apkDownloadService.cancelDownload();
    if (this.downloadSubscription) {
      this.downloadSubscription.unsubscribe();
    }
  }
}

